<template>
    <div>
        <!-- Email transfert -->
        <div
            ref="emailSettingsAccountModeTransfer"
            class="flex-row jc-space ai-center p-3 bt-1 transfert_email"
        >
            <div class="flex-column left">
                <div class="typographies-paragraph-medium-bold">
                    {{ $t.__('email.EmailSettingsAccountModeTransfer.transfer_email') }}
                </div>
                <div class="typographies-paragraph-medium-regular">
                    {{ `corp${corpId}s${userId}@sellsy.net` }}
                </div>
                <div class="transfert_email-caption-help-link typographies-heading-h4">
                    {{ $t.__('email.EmailSettingsAccountModeTransfer.connect_an_account') }}

                    <CompassLink :label="$t.__('common.labels.learn_more')" />
                </div>
            </div>
            <router-link v-if="isAdmin || !isEmailsSentFromCorp" :to="{ name: 'onboarding.email' }">
                <CompassButton
                    :label="$t.__('email.configure_email_service')"
                    :type="isOnboardingSetup ? 'secondary' : 'primary'"
                />
            </router-link>
        </div>

        <!-- Email Expédition -->
        <div class="p-3 bt-1 sending-email">
            <div class="wp-10">
                <p class="typographies-paragraph-medium-bold mb-1 mt-0">
                    {{ $t.__('email.EmailSettingsAccountModeTransfer.sending_adress_email') }}
                </p>
                <CompassAlert
                    v-if="!emailIsVerified && (isAdmin || !isEmailsSentFromCorp)"
                    type="warning"
                    :showClose="false"
                    :description="
                        $t.__(`email.EmailSettingsAccountModeTransfer.confirm_email_adress`)
                    "
                    :action="{
                        label: $t.__('email.EmailSettingsAccountModeTransfer.check_now'),
                        callback: () => openPopupCheckSendingEmail('checking'),
                    }"
                />
            </div>
            <div class="flex-level mt-2">
                <div class="typographies-paragraph-medium-regular wp-6">{{ textSenderEmail }}</div>
                <div class="wp-3 flex-item-right">
                    <div class="flex-row ai-center" style="justify-content: end">
                        <div v-if="!emailIsVerified" class="no-shrink mr-2">
                            <CompassIcon icon="warning-fill" type="warning" />
                        </div>
                        <CompassTooltip
                            :content="
                                $t.__('email.EmailSettingsAccountModeTransfer.pending_verification')
                            "
                            :disabled="!shouldDisplayTooltip"
                        >
                            <div class="typographies-paragraph-medium-regular">
                                {{ senderEmail }}
                            </div>
                        </CompassTooltip>
                        <CompassButton
                            :disabled="!isAdmin && isEmailsSentFromCorp"
                            :label="$t.__('common.actions.edit')"
                            class="ml-3"
                            type="secondary"
                            @click="openPopupCheckSendingEmail('edit')"
                        />
                    </div>
                    <el-input v-model="newSenderEmail" name="senderEmail" type="hidden"></el-input>
                </div>
            </div>
            <div
                v-if="isPendingEmail && !isPendingEmailVerificationOnActualSender"
                class="flex-row mt-2 flex-right"
            >
                <CompassIcon icon="warning-fill" size="small" type="warning" />
                <div
                    ref="checkingEmailPendingMessage"
                    class="typographies-paragraph-small-regular gray-80 ml-1"
                >
                    {{ pendingMessage }}
                </div>
            </div>
            <div v-if="isSavingEmailNeeded" class="flex-row mt-2 flex-right">
                <CompassIcon icon="warning-fill" size="small" type="warning" />
                <div
                    ref="sendingEmailShowSaveNeededMessage"
                    class="typographies-paragraph-small-regular aright gray-80 ml-1"
                >
                    {{
                        $t.__(
                            'email.EmailSettingsAccountModeTransfer.save_preferences',
                            newSenderEmail,
                        )
                    }}
                </div>
            </div>
        </div>

        <!-- Domaine -->
        <div v-if="isActiveDomainSection" class="flex-level p-3 bt-1">
            <div class="wp-6">
                <p class="bold mb-1 mt-0">
                    {{ $t.__('email.EmailSettingsAccountModeTransfer.status_of_my_domain') }}
                </p>
                <p class="grey my-0 mr-1">
                    {{
                        $t.__(
                            'email.EmailSettingsAccountModeTransfer.authenticated_and_properly_configured_domain',
                        )
                    }}
                </p>
                <p class="mb-0 mt-1">
                    <a
                        class="link mb-0 mt-0 fs-3"
                        href="https://help.sellsy.com/hc/receiving-and-sending-emails-with-sellsy/set-up-your-domain-for-better-deliverability?searched=true"
                        target="_blank"
                    >
                        {{
                            $t.__(
                                'email.EmailSettingsAccountModeTransfer.help_with_domain_authentication',
                            )
                        }}
                    </a>
                </p>
            </div>

            <div class="wp-4 flex-row jc-space">
                <div class="ml-6">
                    <p class="grey m-0">{{ $t.__('common.labels.status') }}</p>
                    <p class="m-0">
                        <span :class="`chip-${domainStatus.type}`" class="chip"></span
                        >{{ domainStatus.message }}
                    </p>
                </div>
                <CompassButton
                    :label="$t.__('email.EmailSettingsAccountModeTransfer.authenticate_domain')"
                    type="secondary"
                    @click="goToSettingsEmailDomain"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { FadeOutIn } from 'utils/transition';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapStateUser, mapActions: userMapActions } = createNamespacedHelpers('user');
const { mapState: mapStateCorp } = createNamespacedHelpers('corp');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import OnboardingEmailApi from 'api/onboarding/email/onboardingEmail.js';

export default {
    name: 'EmailSettingsAccountModeTransfer',
    props: {
        isEmailsSentFromCorp: {
            type: Boolean,
            required: true,
            default: false,
        },
        selectedProvider: {
            type: String,
            required: true,
            default: '',
        },
    },
    data() {
        return {
            domainStatus: {
                type: 'warning',
                message: this.$t.__('common.labels.verification_in_progress'),
            },
            newSenderEmail: '',
            hasEmailVerifiedEvent: false,
            eventData: null,
            hasCheckedDomainStatus: false,
        };
    },
    computed: {
        ...mapStateUser({
            userId: 'id',
            isAdmin: 'isAdmin',
            senderEmail: 'senderEmail',
            pendingEmail: 'pendingEmail',
            emailIsVerified: 'emailIsVerified',
            isOnboardingSetup: 'isOnboardingSetup',
        }),
        ...mapStateCorp({
            corpId: 'id',
        }),
        isPendingEmail() {
            return this.pendingEmail !== null;
        },
        isSavingEmailNeeded() {
            return this.isPendingEmail
                ? this.newSenderEmail !== this.pendingEmail
                : this.senderEmail !== this.newSenderEmail;
        },
        /**
         * We want to display tooltip only when user have setup his onbaording but  haven't verified his email
         * @returns boolean
         */
        shouldDisplayTooltip() {
            return this.isOnboardingSetup && !this.emailIsVerified;
        },
        /**
         * Return true only if the provider selected during configuration is 'other'
         */
        isActiveDomainSection() {
            return this.selectedProvider === 'other';
        },
        pendingMessage() {
            return this.$attrs.pendingEmail
                ? `${this.$attrs.pendingEmail} ${this.$t.__(
                    'email.EmailSettingsAccountModeTransfer.pending_message',
                )}`
                : '';
        },
        isPendingEmailVerificationOnActualSender() {
            return this.pendingEmail === this.senderEmail;
        },
        /*/**
         * Return the domain of the emailSender
         */
        domain() {
            if (this.senderEmail) {
                return this.senderEmail.split('@').pop();
            }
            return '';
        },
        textSenderEmail() {
            return this.isEmailsSentFromCorp
                ? this.$t.__(
                    'email.EmailSettingsAccountModeTransfer.text_sender_email_corp_account',
                )
                : this.$t.__(
                    'email.EmailSettingsAccountModeTransfer.text_sender_email_staff_account',
                );
        },
    },
    mounted() {
         
        EventBus.on(
            'EmailOnboardingDomain::OnboardingEmailApi::findDns::isValidDomainAuth',
            (isValid) => {
                if (this.hasCheckedDomainStatus) {
                    return;
                }
                this.domainStatus = isValid
                    ? { type: 'success', message: this.$t.__('common.labels.authenticated') }
                    : { type: 'error', message: this.$t.__('common.labels.not_authenticated') };
                this.hasCheckedDomainStatus = true;
            },
        );
        this.newSenderEmail = this.pendingEmail ?? this.senderEmail;
    },
    created() {
        document.addEventListener('visibilitychange', () => this.visibilitychangeEvent());
        EventBus.on('checkingEmail:senderEmailChange', this.changeEmailSender);
        EventBus.on('email:onEmailVerified', this.onEmailVerifiedEvent);
    },
    unmounted() {
        document.removeEventListener('visibilitychange', () => this.visibilitychangeEvent());
        EventBus.off('checkingEmail:senderEmailChange', this.changeEmailSender);
        EventBus.off('email:onEmailVerified', this.onEmailVerifiedEvent);
    },
    methods: {
        /**
         * Temporary... as long as xsl is used before use vue router and SettingsEmailDomain component like a page
         * Make the xsl disappear and then the component appear
         */
        ...popupPanelMapActions(['open']),
        ...userMapActions(['updateSenderEmail', 'updatePendingEmail', 'updateEmailIsVerified']),
        openPopupCheckSendingEmail(mode) {
            this.open({
                key: 'popupCheckSendingEmail',
                props: {
                    mode,
                    senderEmail: this.senderEmail,
                },
            });
        },
        goToSettingsEmailDomain() {
            OnboardingEmailApi.authDomainValidation({ domain: this.domain }).then(() => {
                const prefsFormEl = document.getElementById('prefsForm');
                const settingsEmailDomainEl = document.getElementById('settingsEmailDomain');

                FadeOutIn(prefsFormEl, settingsEmailDomainEl);
            });
        },
        // Used by events
         
        changeEmailSender(event, param) {
            this.newSenderEmail = param;
        },
         
        onEmailVerifiedEvent(event) {
            this.hasEmailVerifiedEvent = true;
            this.eventData = event;
            if (document.visibilityState === 'visible') {
                this.triggerEventEmailVerified();
            }
        },
        visibilitychangeEvent() {
            if (document.visibilityState === 'visible' && this.hasEmailVerifiedEvent) {
                this.triggerEventEmailVerified();
            }
        },
        triggerEventEmailVerified() {
            this.updatePendingEmail(null);
            if (this.eventData.success) {
                this.updateSenderEmail(this.eventData.senderEmail);
                this.updateEmailIsVerified(true);
                this.newSenderEmail = this.eventData.senderEmail;
            }
            this.$pushToast({
                type: this.eventData.success ? 'success' : 'error',
                text: this.eventData.message,
                close: true,
                icon: true,
                unique: true,
                timeout: 4000,
            });
            this.hasEmailVerifiedEvent = false;
        },
    },
};
</script>
<style lang="scss" scoped>
.transfert_email {
    .flex-column.left {
        gap: 4px;
    }
    &-caption-help-link {
        color: var(--colors-common-text-standard-caption);
    }
}
</style>
